import type { EchoflicksSessionEvent } from "../types";

/**
 * RE Studio music (TRK-240): the song a film uses. Two events carry it on the
 * film's own session (workspace contract
 * plans/2026-10-16-re-studio-music/contracts.md, sections 5.6 and 7.1):
 *
 * - `studio.music.track.used` (bot): the agent picked the song at create,
 *   `{track_id, title, source: generated|library, retention, generation_id,
 *   format_id, ...}`;
 * - `director.soundtrack_resolved` (orchestrator): the film's music node took
 *   it, `{source, track_id, title, sha256}`.
 *
 * The indexer labels their journey node "Soundtrack: {title} ({source})" with
 * the same source words as below, so the journey and the event list agree.
 */
export const TRACK_USED_EVENT = "studio.music.track.used";
export const SOUNDTRACK_RESOLVED_EVENT = "director.soundtrack_resolved";

const SOURCE_WORDS: Record<string, string> = {
  generated: "composed",
  library: "library",
  agent: "agent's pick",
  saved: "saved song",
  retained: "composed",
};

const SOURCE_TOKEN = /^[a-z][a-z0-9_]{0,31}$/;
const TITLE_MAX = 80;

export type SoundtrackFacts = {
  title: string | null;
  /** The raw source token, e.g. "generated". */
  source: string | null;
  /** How the CRM reads it, e.g. "composed". */
  sourceLabel: string | null;
  trackId: string | null;
};

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.replace(/\s+/g, " ").trim();
  return trimmed || null;
}

export function soundtrackFacts(payload: unknown): SoundtrackFacts {
  const record =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : {};
  const rawTitle = text(record.title);
  const title =
    rawTitle && rawTitle.length > TITLE_MAX
      ? `${rawTitle.slice(0, TITLE_MAX - 3).trimEnd()}...`
      : rawTitle;
  const sourceText = text(record.source)?.toLowerCase() ?? null;
  const source =
    sourceText && SOURCE_TOKEN.test(sourceText) ? sourceText : null;
  return {
    title,
    source,
    sourceLabel: source ? (SOURCE_WORDS[source] ?? source) : null,
    trackId: text(record.track_id),
  };
}

/** "Golden Hour (composed)", from whichever parts are present. */
function songLabel(facts: SoundtrackFacts): string | null {
  if (facts.title && facts.sourceLabel) {
    return `${facts.title} (${facts.sourceLabel})`;
  }
  return facts.title ?? (facts.sourceLabel ? `(${facts.sourceLabel})` : null);
}

/** The event's payload: `raw_payload.payload` when the row nests it, else `raw_payload`. */
function eventPayload(
  event: Pick<EchoflicksSessionEvent, "raw_payload">,
): Record<string, unknown> {
  const raw = event.raw_payload;
  if (!raw || typeof raw !== "object") return {};
  const nested = (raw as Record<string, unknown>).payload;
  return nested && typeof nested === "object"
    ? (nested as Record<string, unknown>)
    : raw;
}

export function isSoundtrackEvent(eventType: string | null | undefined) {
  return (
    eventType === TRACK_USED_EVENT || eventType === SOUNDTRACK_RESOLVED_EVENT
  );
}

/**
 * The events list's label: "Soundtrack used: Golden Hour (composed)" for the
 * soundtrack events, the event type with spaces for everything else.
 */
export function sessionEventLabel(
  event: Pick<EchoflicksSessionEvent, "event_type" | "raw_payload">,
): string {
  const eventType = event.event_type ?? "";
  if (!isSoundtrackEvent(eventType)) return eventType.replaceAll("_", " ");
  const verb = eventType === TRACK_USED_EVENT ? "used" : "resolved";
  const song = songLabel(soundtrackFacts(eventPayload(event)));
  return song ? `Soundtrack ${verb}: ${song}` : `Soundtrack ${verb}`;
}

/** "track trk_…", the second line of a soundtrack event; null otherwise. */
export function sessionEventDetail(
  event: Pick<EchoflicksSessionEvent, "event_type" | "raw_payload">,
): string | null {
  if (!isSoundtrackEvent(event.event_type)) return null;
  const { trackId } = soundtrackFacts(eventPayload(event));
  return trackId ? `track ${trackId}` : null;
}
